/**
 * The dialog's shared styles
 */

/**
 * A floor under the content, so the dialog stays one size across the stages. Without it the
 * box resizes under the pointer and the buttons move as the user reaches for them.
 *
 * A floor rather than a fixed height: a stage that outgrows it still gets its room.
 */
const CONTENT_MIN_HEIGHT = 320;

/** Content the modes lay out inside that floor, rather than under it. */
export const dialogContentSx = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: CONTENT_MIN_HEIGHT,
    boxSizing: 'border-box',
    backgroundColor: 'background.paper',
};

/** The rails top and bottom. The tint steps them back so the content comes forward. */
export const dialogRailSx = { backgroundColor: 'grey.50' };

/** A control rather than a glyph loose on the title bar, since it sits on the tint. */
export const dialogCloseSx = {
    flexShrink: 0,
    backgroundColor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1.5,
    boxShadow: 1,
    p: 0.75,
    '&:hover': { backgroundColor: 'grey.100', boxShadow: 2 },
};

/**
 * The tabs as a rail of their own, pulled out through the content's padding so the strip
 * reads as a band under the title rather than two words floating above the list.
 */
export const dialogTabsRailSx = {
    mx: -3,
    mt: -2,
    mb: 2,
    px: 1,
    backgroundColor: 'grey.50',
    borderBottom: '1px solid',
    borderColor: 'divider',
};

export const dialogTabsSx = {
    minHeight: 44,
    '& .MuiTab-root': {
        minHeight: 44,
        textTransform: 'none',
        fontSize: '0.9rem',
        fontWeight: 500,
    },
    '& .MuiTab-root.Mui-selected': { fontWeight: 700 },
    '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
};

/**
 * One block of the share sheet, and the confirmation's message. The same bordered surface
 * as the chart pane, so a stage of loose fields reads as a few things rather than a column.
 */
export const dialogSectionSx = {
    p: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    boxShadow: 1,
};

/**
 * The stage-ending buttons — Cancel, Close, Done, and the detour beside them.
 *
 * Outlined and raised, because on the tinted rail a bare text button reads as printed words
 * rather than something to press. The confirming action stays `contained`, so the pair still
 * reads in order.
 */
export const dialogDismissButtonSx = {
    backgroundColor: 'background.paper',
    boxShadow: 1,
    '&:hover': { backgroundColor: 'background.paper', boxShadow: 2 },
};

/** The list fills the floor and scrolls inside it, rather than setting the dialog's height. */
export const dialogListSx = { flex: 1, minHeight: 0, overflow: 'auto', py: 0 };

/** Roomier than the shared section box: these two hold a control each, not one message. */
export const shareSectionSx = { ...dialogSectionSx, p: 3 };

/** Section headings inside the share stage: small, quiet, and paired with an icon. */
export const shareHeadingSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mb: 1.25,
    color: 'text.secondary',
    '& .MuiSvgIcon-root': { fontSize: 18 },
};

/** The link is a value to copy, not prose — so it reads as one. */
export const shareLinkInputSx = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.8rem',
    backgroundColor: 'grey.50',
};

/** The alphabetical rails down the option list. */
export const sharePopperSx = {
    '& .MuiAutocomplete-groupLabel': {
        lineHeight: '32px',
        fontSize: '1rem',
        fontWeight: 800,
        letterSpacing: '0.08em',
        color: 'text.primary',
        backgroundColor: 'grey.50',
        borderBottom: '1px solid',
        borderColor: 'divider',
    },
};
